import { type SerializedSpainClosurePeriod } from '@spendesk/bookkeeping-core-types';
import { isDate, isWithinInterval } from 'date-fns';

import type { Payable } from 'modules/bookkeep/payables/containers/PayablePanelContainer/hooks/usePayableQuery/payable';
import { useFeature } from 'src/core/common/hooks/useFeature';
import FEATURES from 'src/core/constants/features';
import { useSpainClosurePeriods } from 'src/core/modules/bookkeep/apis/useSpainClosurePeriods';

export type SpanishDprArchivingStatus =
  | {
      isArchived: true;
      state: 'pending' | 'ongoing' | 'completed' | 'failed';
      from: SerializedSpainClosurePeriod['from'];
      to: SerializedSpainClosurePeriod['to'];
    }
  | { isArchived: false };

export const useSpanishDprArchived = (
  payableData?: Pick<Payable, 'accountingDate' | 'documentaryEvidence'>,
): SpanishDprArchivingStatus => {
  const spanishDprEnabled = useFeature(FEATURES.SPANISH_DPR);
  const spanishDprPhase2Enabled = useFeature(FEATURES.TMP_SPANISH_DPR_PHASE_2);

  const { periods: closurePeriods } = useSpainClosurePeriods();
  if (!payableData || !spanishDprEnabled || !spanishDprPhase2Enabled) {
    return { isArchived: false };
  }
  const documentaryEvidence = payableData.documentaryEvidence;

  if (!documentaryEvidence) {
    return { isArchived: false };
  }
  const compliances = documentaryEvidence.compliances;

  if (!compliances?.spainEreceipt) {
    return { isArchived: false };
  }
  const accountingDate = payableData.accountingDate;
  if (!accountingDate || !isDate(accountingDate)) {
    return { isArchived: false };
  }

  if (closurePeriods.length === 0) {
    return { isArchived: false };
  }

  const closurePeriod = closurePeriods.find((period) =>
    isWithinInterval(accountingDate, { start: period.from, end: period.to }),
  );

  if (!closurePeriod || closurePeriod.state === 'failed') {
    return { isArchived: false };
  }

  return {
    isArchived: true,
    state: closurePeriod.state,
    from: closurePeriod.from,
    to: closurePeriod.to,
  };
};
